import { useDispatch, useSelector } from 'react-redux';
import { InfoGetDTO, InfoState, TableFriendlyName } from '../info/types';
import tableSlice from './slice';
import { CustomColumnsDataGet, TableState } from './types';
import { selectInfo, selectLabelState, selectSubmissionState, selectTable } from '../selectors';
import { SubmissionState } from '../submission/types';
import { useEffect } from 'react';
import { LabelState } from '../label/types';
import { Model } from '../../components/MyGridRedux/types';
import labelSlice from '../label/slice';
import submissionSlice from '../submission/slice';
import infoSlice from '../info/slice';
import { can } from '../../helpers/permissions';

function useFetchTableData(
  friendlyName: TableFriendlyName,
  queryParams?: { [key: string]: any },
): [() => void, (() => void) | undefined, (data: InfoGetDTO) => void] {
  const dispatch = useDispatch();
  const { tablesSettings, customColumnsData }: TableState = useSelector(selectTable);
  const { submissionsById }: SubmissionState = useSelector(selectSubmissionState);
  const { info }: InfoState = useSelector(selectInfo);
  const { labelables }: LabelState = useSelector(selectLabelState);

  const { model } = tablesSettings[friendlyName];
  const canAccessSummarizedViews = can('ACCESS_SUMMARIZED_VIEWS');

  const getSubmissionsAction = () => dispatch(submissionSlice.actions.GET_SUBMISSIONS());

  const getInfoAction = (data: InfoGetDTO) => dispatch(infoSlice.actions.GET(data));

  const getCustomColumnsDataAction = (data: CustomColumnsDataGet) =>
    dispatch(tableSlice.actions['CUSTOMCOLUMNS:DATA:GET'](data));

  const getLabelablesAction = (type: Model) => dispatch(labelSlice.actions['LABELABLES:GET'](type));

  const fetchData = () => {
    if (model == 'App\\Models\\Submission') {
      getSubmissionsAction();
    } else if (model == 'App\\Models\\Person') {
      getInfoAction({ friendlyName: 'users' });
    }
    if (friendlyName != 'users') {
      getInfoAction({ friendlyName, queryParams });
    }
  };

  const fetchCustomColumnsData = canAccessSummarizedViews
    ? () => {
        getCustomColumnsDataAction({ tableFriendlyName: friendlyName, queryParams });
      }
    : undefined;

  const isDataMissing = (): boolean => {
    let data;
    switch (model) {
      case 'App\\Models\\Invitation':
        data = info['invitations'].byId;
        break;
      case 'App\\Models\\Person':
        data = info['users'].byId;
        break;
      case 'App\\Models\\Submission':
        data = submissionsById;
        break;
    }
    return Object.keys(data).length == 0;
  };

  const canAggregate = (
    [
      'submissions',
      'reviewers_bids',
      'submissions_bids',
      'reviews_to_papers',
      'papers_to_reviews',
      'reviewer_assignments',
      'submission_assignments',
      'submissions_to_review',
    ] as TableFriendlyName[]
  ).includes(friendlyName);

  /* Data fetching */
  useEffect(() => {
    if (isDataMissing()) {
      fetchData();
    } else if (
      (
        ['submission_bids', 'reviewer_bids', 'submission_assignments', 'reviewer_assignments'] as TableFriendlyName[]
      ).includes(friendlyName) &&
      Object.keys(info[friendlyName].byId).length == 0
    ) {
      getInfoAction({ friendlyName, queryParams });
    } else if (canAggregate && !customColumnsData[friendlyName] && fetchCustomColumnsData) {
      fetchCustomColumnsData();
    }

    if (friendlyName != 'submissions_to_review' && !Object.keys(labelables[model]).length) {
      getLabelablesAction(model);
    }
  }, []);

  return [fetchData, fetchCustomColumnsData, getInfoAction];
}

export default useFetchTableData;
